import { nodesFlux } from '@candulabs/craft';
import React, { useMemo } from 'react';

import { toCssSelector } from '@candu-ai/stylesheet/utils';
import Node from 'craft/components/Node';
import {
  createVariantsSelector,
  NodeTextMenu,
  Separator,
  TextAlignmentButtons,
} from 'craft/components/SelectedToolbar';
import { TypographyAI } from 'craft/components/SelectedToolbar/AskAI/TypographyAI';
import { BuiltInComponents, TypographyVariants } from 'craft/constants';
import { useEditorSelector, useNodeContext } from 'craft/hooks';
import { CraftNode, Resolver, ToolboxComponentSections } from 'craft/types';

import { TypographyStyles, TypographyVariantSelector } from './toolbox';

export const Typography: Resolver = (props: any) => {
  const { id: nodeId } = useNodeContext();
  const customSelector = useMemo(() => nodesFlux.selectors.getNodeCustom(nodeId), [nodeId]);

  const { variant, variation } = useEditorSelector(customSelector).custom ?? {};

  const Comp = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'p'].includes(
    variant?.toLowerCase() ?? '',
  )
    ? variant?.toLowerCase()
    : 'p';

  const typographyVariant = {
    variant: variation || variant || 'base',
  };

  const classNameVariant = toCssSelector(`typography`, {
    variant: typographyVariant,
    output: 'html',
  });

  const propsWithVariant = {
    ...props,
    className: `${props.className || ''} ${classNameVariant}`,
  };

  return <Node is={Comp} {...propsWithVariant} />;
};

Typography.craft = {
  name: BuiltInComponents.Typography,
  isCanvas: false,
};

Typography.slate = {
  toCraftNode: (slateNode: any) => (craftNode: any) => {
    // eslint-disable-next-line
    craftNode.data.custom = {
      ...craftNode.data.custom,
      collections: slateNode.data.collections,
      css: slateNode.data.css,
      cssSelectors: slateNode.data.cssSelectors,
      custom: {
        ...(craftNode.data.custom?.custom ?? {}),
        variant: slateNode.data.variant,
        variation: slateNode.data.variation,
      },
    };
  },
  toSlateNode: (craftNode: CraftNode) => (slateNode: any) => {
    // eslint-disable-next-line
    slateNode.data = {
      variant: craftNode.data.custom?.custom?.variant ?? TypographyVariants.P,
      variation: craftNode.data.custom?.custom?.variation,

      css: craftNode.data.custom?.css ?? {},
      cssSelectors: craftNode.data.custom?.cssSelectors ?? {},
      collections: craftNode.data.custom?.collections,
      isNodeDisabled: (craftNode.data.custom?.collections || []).length > 0,
    };
  },
};

Typography.toolbox = {
  schema: {
    [ToolboxComponentSections.NameAndType]: {
      variantSelector: TypographyVariantSelector,
    },
    [ToolboxComponentSections.Layout]: {
      blockedDisplays: ['flex', 'inline-block'],
    },
    [ToolboxComponentSections.Size]: {
      hasOverflow: true,
    },
  },
  component: [TypographyStyles],
  responsiveComponent: [TypographyStyles],
};

Typography.selectedToolbar = {
  customItems: [
    NodeTextMenu,
    TextAlignmentButtons,
    Separator,
    createVariantsSelector({
      getConditions: (props, custom) => {
        const { variant, variation } = custom;
        return { name: variation || variant, ...custom };
      },
      componentName: 'typography',
      variantToCustom: (variant) => variant.conditions,
    }),
    TypographyAI,
  ],
};
